import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Phone, Sparkles, Coins } from "lucide-react";

const NO_MERCHANT = "__none__";

export default function Landing() {
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get("ref");
  const [merchants, setMerchants] = useState([]);
  const [refMerchant, setRefMerchant] = useState(null);
  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    telephone: "",
    email: "",
    code_postal: "",
    merchant_id: NO_MERCHANT,
    consent: false,
    consent_data_sharing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [agencyInfo, setAgencyInfo] = useState({
    phone_display: "09 80 23 80 80",
    phone_raw: "0980238080",
    agency_name: "Place des Énergies — Siège",
    source: "siege",
  });

  useEffect(() => {
    const url = refParam ? `/landing-info?ref=${encodeURIComponent(refParam)}` : "/landing-info";
    api.get(url).then(({ data }) => setAgencyInfo(data)).catch(() => {});
  }, [refParam]);

  useEffect(() => {
    api.get("/merchants?public=true").then(({ data }) => {
      setMerchants(data || []);
      if (refParam) {
        const found = (data || []).find((m) => m.id === refParam);
        if (found) {
          setRefMerchant(found);
          setForm((f) => ({ ...f, merchant_id: found.id }));
        }
      }
    }).catch(() => setMerchants([]));
  }, [refParam]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent) {
      setError("Merci d'accepter le consentement RGPD pour continuer.");
      return;
    }
    if (!form.consent_data_sharing) {
      setError("Merci d'accepter la transmission de vos coordonnées au commerçant partenaire.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        telephone: form.telephone.trim(),
        email: form.email.trim(),
        code_postal: form.code_postal.trim(),
        consent: true,
        consent_data_sharing: true,
      };
      if (form.merchant_id && form.merchant_id !== NO_MERCHANT) {
        payload.merchant_id = form.merchant_id;
      }
      if (refParam) payload.ref = refParam;
      const { data } = await api.post("/leads", payload);
      setSuccess(data);
      toast.success("Votre demande a bien été enregistrée.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const tradeEmoji = (t) => {
    const k = (t || "").toLowerCase();
    if (k.includes("boulang")) return "🥖";
    if (k.includes("bouch")) return "🥩";
    if (k.includes("resto") || k.includes("restau")) return "🍽️";
    return "🛍️";
  };

  const merchantOptions = useMemo(() => {
    // When landing was opened via a QR (refParam set): only expose the QR merchant + "no specific merchant"
    // to avoid confusing the client with an irrelevant full list.
    if (refParam) {
      const refM = merchants.find((m) => m.id === refParam);
      return [
        { id: NO_MERCHANT, label: "Sans commerçant spécifique", emoji: "" },
        ...(refM
          ? [{
              id: refM.id,
              label: `${refM.nom} — ${refM.ville}`,
              emoji: tradeEmoji(refM.trade || refM.trade_label),
            }]
          : []),
      ];
    }
    return [
      { id: NO_MERCHANT, label: "Sans commerçant spécifique", emoji: "" },
      ...merchants.map((m) => ({
        id: m.id,
        label: `${m.nom} — ${m.ville}`,
        emoji: tradeEmoji(m.trade || m.trade_label),
      })),
    ];
  }, [merchants, refParam]);

  const steps = [
    { n: "01", html: <><strong>Vous demandez à être rappelé·e</strong> — sans engagement.</> },
    { n: "02", html: <>Un conseiller vous propose une <strong>offre d'énergie jusqu'à 20% moins chère*</strong>.</> },
    { n: "03", html: <>Une fois votre souscription validée, <strong>votre bon d'achat</strong> est crédité chez <strong>votre commerçant</strong>. Vous n'avez plus qu'à <strong>en profiter</strong>&nbsp;!</> },
  ];

  return (
    <div className="min-h-screen bg-[#F5F6F9]" data-testid="landing-page">
      {/* Header — vraie barre blanche */}
      <header className="w-full bg-white border-b border-[#BFC6CC]">
        <div className="max-w-6xl mx-auto h-[88px] px-8 sm:px-10 flex items-center justify-between">
          <Logo size="md" />
          <Link
            to="/login"
            className="text-[14px] text-[#397DFF] font-semibold hover:underline tracking-[0.01em]"
            data-testid="landing-login-link"
          >
            Espace pro →
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* LEFT — Hero */}
        <section className="space-y-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-[#191D1E]"
            style={{
              background: "rgba(249, 200, 14, 0.12)",
              border: "1px solid rgba(249, 200, 14, 0.4)",
            }}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#F9C80E]" /> Offre commerce de proximité
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#191D1E] leading-[1.05]" style={{ letterSpacing: "-0.02em" }}>
            Votre énergie moins chère +{" "}
            <span className="text-[#F9C80E]">30&nbsp;€<sup className="text-[0.5em] align-super">*</sup> offerts</span>{" "}
            chez votre commerçant
          </h1>

          <p className="text-[16px] text-[#191D1E]/85 max-w-[480px] leading-[1.55]">
            Devenez client <strong className="font-semibold">Place des Énergies</strong> et recevez{" "}
            <strong className="font-semibold">30&nbsp;€<sup>*</sup> offerts</strong> en bon d'achat à dépenser chez votre commerçant.
          </p>

          {/* 3 étapes style valeurs brandbook */}
          <ol className="space-y-6 pt-2">
            {steps.map((s) => (
              <li key={s.n} className="flex items-start gap-6">
                <span className="font-display font-bold text-[56px] leading-none text-[#397DFF] tabular-nums" style={{ letterSpacing: "-0.03em" }}>
                  {s.n}
                </span>
                <span className="text-[15px] text-[#191D1E] pt-2 leading-[1.55] max-w-[420px]">{s.html}</span>
              </li>
            ))}
          </ol>

          {/* Bloc téléphone */}
          <div
            className="flex items-center gap-5 rounded-2xl bg-white px-6 py-5 max-w-[480px]"
            style={{ border: "1px solid #BFC6CC" }}
            data-testid="landing-phone-block"
          >
            <div
              className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: "rgba(57,125,255,0.08)" }}
            >
              <Phone className="w-5 h-5 text-[#397DFF]" strokeWidth={2} />
            </div>
            <div className="flex flex-col min-w-0">
              <div className="text-[13px] font-medium text-[#687175]">
                Vous préférez nous appeler&nbsp;?
              </div>
              <a
                href={`tel:${agencyInfo.phone_raw}`}
                className="font-display font-bold text-[#191D1E] hover:text-[#397DFF] transition-colors leading-[1.1] mt-0.5"
                style={{ fontSize: "24px", letterSpacing: "-0.01em" }}
                data-testid="landing-phone-link"
              >
                {agencyInfo.phone_display}
              </a>
              <div className="text-[12px] text-[#687175] mt-1">
                {agencyInfo.source === "licencie" ? (
                  <>Joignez votre agence <strong className="text-[#191D1E] font-semibold">{agencyInfo.agency_zone || agencyInfo.agency_name}</strong> · Appel non surtaxé</>
                ) : (
                  <><strong className="text-[#191D1E] font-semibold">Place des Énergies</strong> · Appel non surtaxé</>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT — Form */}
        <section className="lg:sticky lg:top-12">
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(25,29,30,0.06)] border border-[#BFC6CC] overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#397DFF] to-[#F9C80E]" />
            <div className="p-8 sm:p-10">
              {!success ? (
                <>
                  <div className="flex items-center gap-2 text-[#397DFF] text-[11px] uppercase tracking-[0.2em] font-semibold mb-3">
                    <Phone className="w-3.5 h-3.5" /> Demande de rappel
                  </div>
                  <h2 className="font-display text-[28px] sm:text-[32px] font-bold text-[#191D1E] leading-[1.15]" style={{ letterSpacing: "-0.015em" }}>
                    Demandez à être rappelé·e
                  </h2>
                  <p className="text-[14px] text-[#687175] mt-3 leading-[1.55]">
                    C'est gratuit et sans engagement. Un conseiller vous rappelle au moment qui vous convient.
                  </p>

                  {refMerchant && (
                    <div
                      className="mt-6 rounded-xl bg-[rgba(249,200,14,0.1)] border border-[rgba(249,200,14,0.4)] px-4 py-3 flex items-center gap-3"
                      data-testid="ref-merchant-banner"
                    >
                      <Coins className="w-5 h-5 text-[#F9C80E]" />
                      <div className="text-sm">
                        <div className="text-[#687175] text-[11px] uppercase tracking-[0.18em] font-semibold">
                          Recommandé par
                        </div>
                        <div className="font-semibold text-[#191D1E]">{refMerchant.nom}</div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="landing-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="l-prenom" className="text-[12px] font-semibold text-[#191D1E]">Prénom</Label>
                        <Input id="l-prenom" data-testid="landing-prenom" value={form.prenom} onChange={onChange("prenom")} required className="h-12 rounded-xl border-[1.5px] border-[#BFC6CC] focus:border-[#397DFF] focus:ring-4 focus:ring-[#397DFF]/12 px-4 text-[14px]" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="l-nom" className="text-[12px] font-semibold text-[#191D1E]">Nom</Label>
                        <Input id="l-nom" data-testid="landing-nom" value={form.nom} onChange={onChange("nom")} required className="h-12 rounded-xl border-[1.5px] border-[#BFC6CC] focus:border-[#397DFF] focus:ring-4 focus:ring-[#397DFF]/12 px-4 text-[14px]" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="l-tel" className="text-[12px] font-semibold text-[#191D1E]">Téléphone</Label>
                        <Input id="l-tel" data-testid="landing-telephone" inputMode="tel" placeholder="06 12 34 56 78" value={form.telephone} onChange={onChange("telephone")} required className="h-12 rounded-xl border-[1.5px] border-[#BFC6CC] focus:border-[#397DFF] focus:ring-4 focus:ring-[#397DFF]/12 px-4 text-[14px]" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="l-cp" className="text-[12px] font-semibold text-[#191D1E]">Code postal</Label>
                        <Input id="l-cp" data-testid="landing-cp" inputMode="numeric" maxLength={5} placeholder="59990" value={form.code_postal} onChange={onChange("code_postal")} required className="h-12 rounded-xl border-[1.5px] border-[#BFC6CC] focus:border-[#397DFF] focus:ring-4 focus:ring-[#397DFF]/12 px-4 text-[14px]" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="l-email" className="text-[12px] font-semibold text-[#191D1E]">E-mail</Label>
                      <Input id="l-email" data-testid="landing-email" type="email" placeholder="vous@email.fr" value={form.email} onChange={onChange("email")} required className="h-12 rounded-xl border-[1.5px] border-[#BFC6CC] focus:border-[#397DFF] focus:ring-4 focus:ring-[#397DFF]/12 px-4 text-[14px]" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="l-merchant" className="text-[12px] font-semibold text-[#191D1E]">Votre commerçant *</Label>
                      <Select
                        value={form.merchant_id}
                        onValueChange={(v) => setForm((f) => ({ ...f, merchant_id: v }))}
                      >
                        <SelectTrigger id="l-merchant" data-testid="landing-merchant" className="w-full h-12 rounded-xl border-[1.5px] border-[#BFC6CC] focus:border-[#397DFF] focus:ring-4 focus:ring-[#397DFF]/12 px-4 text-[14px]">
                          <SelectValue placeholder="Choisir un commerçant" />
                        </SelectTrigger>
                        <SelectContent>
                          {merchantOptions.map((m) => (
                            <SelectItem key={m.id} value={m.id} data-testid={`landing-merchant-opt-${m.id}`}>
                              <span className="inline-flex items-center gap-2">
                                {m.emoji && <span aria-hidden="true">{m.emoji}</span>}
                                <span>{m.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <label className="flex items-start gap-3 pt-1 cursor-pointer select-none">
                      <Checkbox
                        checked={form.consent}
                        onCheckedChange={(v) => {
                          setForm((f) => ({ ...f, consent: Boolean(v) }));
                          if (v && error) setError(null);
                        }}
                        data-testid="landing-consent"
                        className="mt-0.5 w-[18px] h-[18px] rounded border-[1.5px] border-[#BFC6CC] data-[state=checked]:bg-[#F9C80E] data-[state=checked]:border-[#F9C80E] data-[state=checked]:text-[#191D1E]"
                      />
                      <span className="text-[12px] text-[#687175] leading-[1.45]">
                        <strong className="text-[#191D1E]">J'accepte d'être recontacté·e par téléphone</strong> par un conseiller Place des Énergies au sujet d'une offre d'énergie. Je peux retirer ce consentement à tout moment. <strong className="text-[#191D1E]">(obligatoire)</strong>
                      </span>
                    </label>

                    <label className="flex items-start gap-3 pt-1 cursor-pointer select-none">
                      <Checkbox
                        checked={form.consent_data_sharing}
                        onCheckedChange={(v) => {
                          setForm((f) => ({ ...f, consent_data_sharing: Boolean(v) }));
                          if (v && error) setError(null);
                        }}
                        data-testid="landing-consent-data-sharing"
                        className="mt-0.5 w-[18px] h-[18px] rounded border-[1.5px] border-[#BFC6CC] data-[state=checked]:bg-[#F9C80E] data-[state=checked]:border-[#F9C80E] data-[state=checked]:text-[#191D1E]"
                      />
                      <span className="text-[12px] text-[#687175] leading-[1.45]">
                        <strong className="text-[#191D1E]">J'accepte que mes coordonnées</strong> (nom, prénom, téléphone, email) soient transmises au commerçant partenaire dans le cadre de l'opération "30&nbsp;€ offerts", afin qu'il puisse m'identifier lors de ma visite. <strong className="text-[#191D1E]">(obligatoire)</strong>
                      </span>
                    </label>

                    {error && (
                      <div data-testid="landing-error" className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      data-testid="landing-submit"
                      disabled={submitting || !form.consent || !form.consent_data_sharing}
                      className="w-full h-12 bg-[#F9C80E] hover:bg-[#E6B600] text-[#191D1E] font-display font-bold rounded-xl text-[15pt] shadow-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Envoi..." : "Je veux être rappelé·e →"}
                    </Button>

                    <div className="text-center text-[13px] text-[#687175] pt-2" data-testid="landing-form-phone">
                      Ou appelez-nous au{" "}
                      <a href={`tel:${agencyInfo.phone_raw}`} className="font-semibold text-[#397DFF] hover:underline">
                        {agencyInfo.phone_display}
                      </a>
                    </div>

                    <div className="pt-5 mt-2 border-t border-[#BFC6CC]">
                      <p className="text-[11px] text-[#687175] leading-[1.5]">
                        *Réduction pouvant atteindre 20 % sur le prix du kWh HT par rapport au Tarif Réglementé de Vente, selon le profil de consommation ; économie non garantie. 30&nbsp;€ offerts en bon d'achat valable chez le commerçant désigné, après souscription effective et délai de rétractation. Voir le <a href="#" className="text-[#397DFF] hover:underline">règlement de l'opération</a>. Vos données sont traitées par Place des Énergies (RGPD).
                      </p>
                      <p className="text-[11px] text-[#687175] leading-[1.5] mt-2" data-testid="landing-rgpd-mention">
                        <strong className="text-[#191D1E]">Transmission des données au commerçant partenaire :</strong> dans le cadre de l'opération "30&nbsp;€ offerts", vos coordonnées (nom, prénom, téléphone, email) sont transmises au commerçant partenaire que vous avez sélectionné, afin qu'il puisse vous identifier lors de votre visite et honorer l'offre. Ces données sont conservées pendant la durée de l'opération et peuvent être supprimées sur simple demande à <a href="mailto:contact@placedesenergies.fr" className="text-[#397DFF] hover:underline">contact@placedesenergies.fr</a>.
                      </p>
                    </div>
                  </form>
                </>
              ) : (
                <div data-testid="landing-success" className="py-6 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-[#065F46]" />
                  </div>
                  <h2 className="font-display text-[28px] font-bold text-[#191D1E]">
                    Merci, c'est noté !
                  </h2>
                  <p className="mt-3 text-[#687175] text-[14px] leading-relaxed">
                    Un conseiller vous rappellera sous <strong>48 h ouvrées</strong>.
                    {success.dev_mode ? (
                      <span className="block mt-2 text-xs text-[#92400E]">
                        Mode développeur : email simulé (SMTP non configuré).
                      </span>
                    ) : (
                      <span className="block mt-2">
                        Un email de confirmation vient de vous être envoyé.
                      </span>
                    )}
                  </p>
                  {success.is_duplicate && (
                    <p className="mt-3 text-xs text-[#92400E] bg-[rgba(249,200,14,0.1)] border border-[rgba(249,200,14,0.4)] rounded-lg px-3 py-2">
                      Une demande similaire est déjà en cours. Nous vous recontacterons rapidement.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-[12px] text-[#939BA0] mt-6">
            En 30 secondes · sans engagement · 100 % gratuit.
          </p>
        </section>
      </main>

      {/* Bandeau légal loi conso */}
      <section className="bg-white border-t border-[#BFC6CC]" data-testid="landing-legal-band">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 text-center text-[13px] text-[#687175] leading-[1.6]">
          Le numéro de téléphone n'est utilisé pour rappeler le client qu'après recueil de son consentement explicite (loi n°&nbsp;2025-594 du 30&nbsp;juin&nbsp;2025). Aucun pourcentage d'économie n'est présenté comme garanti.
        </div>
      </section>

      <footer className="border-t border-[#BFC6CC] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-[#939BA0]">
          <div>© Place des Énergies — Réseau bons d'achat commerce local</div>
          <Link to="/login" className="hover:text-[#397DFF]">Accès Espace pro</Link>
        </div>
      </footer>
    </div>
  );
}
